/**
 * Make optional properties in `T` required but possibly `undefined`.
 *
 * Converts
 * ```ts
 * type X = {
 *   a: number;
 *   b?: number;
 *   c: number | undefined;
 * }
 * ```
 * to
 * ```ts
 * Complete<X> == {
 *   a: number;
 *   b: number | undefined;
 *   c: number | undefined;
 * }
 * ```
 */
export type Complete<T> = {
  [P in keyof Required<T>]: Pick<T, P> extends Required<Pick<T, P>> ? T[P] : T[P] | undefined;
};

/**
 * Make some properties in T partial
 */
export type PartialProperties<T, K extends keyof T> = Omit<T, K> & {
  [P in K]: Partial<T[P]>;
};

/**
 * Make some properties in T optional
 */
export type OptionalProperties<T, K extends keyof T> = Omit<T, K> & {
  [P in K]?: T[P];
};

export const isTruthy = <T>(
  value: T | null | undefined | '' | 0 | false,
): value is Exclude<T, null | undefined | '' | 0 | false> => !!value;

export const shallowUpdateIfExists = <T extends string | number | symbol, V>(
  dict: Record<T, V | undefined>,
  key: T,
  data: Partial<V>,
) => {
  const entryToUpdate: V | undefined = dict[key];
  if (entryToUpdate) {
    dict[key] = {
      ...entryToUpdate,
      ...data,
    };
  }
};

export const shallowUpdateIfExistsOrAdd = <T extends string | number | symbol, V>(
  dict: Record<T, V | undefined>,
  key: T,
  data: V,
) => {
  dict[key] = {
    ...(dict[key] || {}),
    ...data,
  };
};

export const addOrOverwrite = <T extends string | number | symbol, V>(
  dict: Record<T, V | undefined>,
  key: T,
  data: V,
) => (dict[key] = data);
